<template>
  <b-modal
    id="MultiBranchAssignDialog"
    ref="MultiBranchAssignDialog"
    title="มอบบทบาทข้ามหลายสาขา"
    size="lg"
    centered
    no-close-on-backdrop
    @hide="onHide"
    @ok.prevent="submit"
  >
    <div v-if="targetUser" class="target-strip mb-3">
      <div class="text-muted small">มอบบทบาทเดียวให้หลายสาขาพร้อมกัน</div>
      <div class="font-weight-bold">
        {{ targetUser.firstNameTh }} {{ targetUser.lastNameTh }}
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-5">
        <b-form-group label="บทบาท" label-class="font-weight-bold">
          <b-form-select
            v-model="roleKey"
            :options="roleOptions"
            :state="roleKey ? true : null"
          />
        </b-form-group>
      </div>
      <div class="col-12 col-md-7">
        <b-form-group
          label="เหตุผล (ใช้กับทุกสาขาที่เลือก ≥10 ตัวอักษร)"
          label-class="font-weight-bold"
          :invalid-feedback="reasonError"
          :state="reasonState"
        >
          <b-form-textarea v-model="reason" rows="1" max-rows="3" :state="reasonState" />
        </b-form-group>
      </div>
    </div>

    <b-form-group :label="branchGroupLabel" label-class="font-weight-bold">
      <div v-if="!roleKey" class="text-muted small">
        เลือกบทบาทก่อนเพื่อกรองสาขาที่มีสิทธิ์มอบหมาย
      </div>
      <div v-else class="branch-list border rounded">
        <div class="branch-list-head">
          <span
            class="cb"
            :class="selectAllClass"
            role="button"
            @click="toggleSelectAll"
          >
            <span class="cb-box"></span>
            เลือก {{ selectedBranchIds.length }}/{{ selectableBranches.length }}
          </span>
        </div>
        <div
          v-for="b in decoratedBranches"
          :key="b.id"
          class="branch-row"
          :class="{ disabled: !b.selectable }"
          @click="b.selectable && toggleBranch(b.id)"
        >
          <span class="cb" :class="{ checked: isSelected(b.id) }">
            <span class="cb-box"></span>
          </span>
          <span class="branch-name">{{ b.name }}</span>
          <span v-if="b.conflict" class="branch-flag conflict">
            <i class="fas fa-exclamation-circle mr-1"></i>มีบทบาทนี้แล้ว
          </span>
          <span v-else-if="!b.allowed" class="branch-flag denied">ไม่มีสิทธิ์</span>
        </div>
        <div v-if="decoratedBranches.length === 0" class="p-3 text-muted small">
          ไม่มีรายการสาขา
        </div>
      </div>
    </b-form-group>

    <!-- Atomic summary bar -->
    <div class="atomic-summary" :class="{ ready: selectedBranchIds.length > 0 }">
      <i class="fas fa-layer-group mr-2"></i>
      <div class="flex-grow-1">
        <div v-if="selectedBranchIds.length > 0">
          จะมอบ <strong>{{ roleLabel }}</strong> ใน
          <strong>{{ selectedBranchIds.length }} สาขา</strong>:
          {{ selectedBranchNames }}
        </div>
        <div v-else>ยังไม่ได้เลือกสาขา</div>
        <div class="small text-muted">
          การกระทำเป็น atomic — ถ้าสาขาใดล้มเหลว จะ rollback ทั้งหมด
        </div>
      </div>
    </div>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-2 mb-0">
      {{ errorMessage }}
    </b-alert>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel">
        ยกเลิก
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSubmit || isSubmitting"
        @click="ok"
      >
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        ยืนยัน ({{ selectedBranchIds.length }})
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { ROLE_LABELS_TH } from "@/constants/roles";

export default {
  name: "MultiBranchAssignDialog",
  data() {
    return {
      targetUser: null,
      clinicUrl: null,
      // [{ id, name }]
      branches: [],
      roleKey: null,
      selectedBranchIds: [],
      reason: "",
      isSubmitting: false,
      errorMessage: "",
    };
  },
  computed: {
    ...mapGetters({
      assignableRolesGetter: "moduleUser/assignableRoles",
      can: "moduleUser/can",
      branchInfo: "moduleUser/getBranchInfo",
    }),
    roleLabel() {
      return ROLE_LABELS_TH[this.roleKey] || this.roleKey || "บทบาท";
    },
    roleOptions() {
      const keys = this.assignableRolesGetter("branch");
      const opts = keys.map(k => ({
        value: k,
        text: ROLE_LABELS_TH[k] || k,
      }));
      return [{ value: null, text: "-- เลือกบทบาท --", disabled: true }, ...opts];
    },
    // All branches annotated with permission + conflict state so the operator
    // can see WHY a branch is unavailable instead of it silently disappearing.
    decoratedBranches() {
      if (!this.roleKey) return [];
      const permKey = `user.assign.${this.roleKey}`;
      const clinicId = this.branchInfo?.clinic?.id;
      const heldBranchIds = new Set(
        (this.targetUser?.branchRoles || [])
          .filter(b => (b.roleKeys || []).includes(this.roleKey))
          .map(b => b.branchId)
      );
      return this.branches.map(b => {
        const allowed = this.can(permKey, { clinicId, branchId: b.id });
        const conflict = heldBranchIds.has(b.id);
        return {
          id: b.id,
          name: b.name,
          allowed,
          conflict,
          selectable: allowed && !conflict,
        };
      });
    },
    selectableBranches() {
      return this.decoratedBranches.filter(b => b.selectable);
    },
    branchGroupLabel() {
      if (!this.roleKey) return "สาขา";
      return `เลือกสาขา (${this.selectableBranches.length}/${this.branches.length} มีสิทธิ์)`;
    },
    selectAllClass() {
      const sel = this.selectedBranchIds.length;
      const total = this.selectableBranches.length;
      if (total > 0 && sel === total) return "checked";
      if (sel > 0) return "partial";
      return "";
    },
    selectedBranchNames() {
      const byId = new Map(this.branches.map(b => [b.id, b.name]));
      return this.selectedBranchIds.map(id => byId.get(id)).filter(Boolean).join(", ");
    },
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canSubmit() {
      return (
        this.roleKey &&
        this.selectedBranchIds.length > 0 &&
        this.reason.trim().length >= 10
      );
    },
  },
  watch: {
    roleKey() {
      // Reset branch selection when role changes — different roles allow different branches.
      this.selectedBranchIds = [];
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedBranchIds.includes(id);
    },
    toggleBranch(id) {
      const i = this.selectedBranchIds.indexOf(id);
      if (i === -1) this.selectedBranchIds.push(id);
      else this.selectedBranchIds.splice(i, 1);
    },
    toggleSelectAll() {
      if (this.selectedBranchIds.length === this.selectableBranches.length) {
        this.selectedBranchIds = [];
      } else {
        this.selectedBranchIds = this.selectableBranches.map(b => b.id);
      }
    },
    show(payload) {
      // payload: { targetUser, clinicUrl, branches: [{id, name}] }
      this.targetUser = payload.targetUser;
      this.clinicUrl = payload.clinicUrl;
      this.branches = payload.branches || [];
      this.roleKey = null;
      this.selectedBranchIds = [];
      this.reason = "";
      this.errorMessage = "";
      this.isSubmitting = false;
      this.$bvModal.show("MultiBranchAssignDialog");
    },
    close() {
      this.$bvModal.hide("MultiBranchAssignDialog");
    },
    onHide() {
      this.errorMessage = "";
      this.isSubmitting = false;
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return;
      this.errorMessage = "";
      this.isSubmitting = true;
      try {
        await axios.post(
          `/api/userMgmt/${this.clinicUrl}/users/${this.targetUser.uid}/bulk-assign`,
          {
            roleKey: this.roleKey,
            branchIds: this.selectedBranchIds,
            reason: this.reason.trim(),
          },
          { skipGlobalErrorHandler: true }
        );
        this.$emit("assigned", {
          uid: this.targetUser.uid,
          roleKey: this.roleKey,
          branchIds: [...this.selectedBranchIds],
        });
        this.close();
      } catch (err) {
        this.errorMessage =
          err.response?.data?.errorMessage?.message ||
          err.response?.data?.message ||
          err.message ||
          "ไม่สามารถมอบบทบาทได้ (การดำเนินการถูก rollback ทั้งหมด)";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.target-strip {
  background: #f6f8fb;
  border: 1px solid #e3e8ef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.branch-list {
  max-height: 240px;
  overflow-y: auto;
  background-color: #fff;
}

.branch-list-head {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  border-bottom: 1px dashed #ced4da;
  padding: 0.5rem 0.75rem;
  z-index: 1;
}

.branch-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
}

.branch-row:last-child {
  border-bottom: 0;
}

.branch-row:hover:not(.disabled) {
  background: #f5f8fc;
}

.branch-row.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.branch-name {
  flex-grow: 1;
}

.branch-flag {
  font-size: 0.8125rem;
}

.branch-flag.conflict {
  color: #c5392e;
}

.branch-flag.denied {
  color: #adb5bd;
  border: 1px dashed #ced4da;
  border-radius: 0.3rem;
  padding: 0 0.4rem;
}

/* hand-rolled checkbox so we can render the indeterminate (partial) state */
.cb {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  user-select: none;
}

.cb-box {
  width: 18px;
  height: 18px;
  border: 2px solid #adb5bd;
  border-radius: 0.25rem;
  display: grid;
  place-items: center;
}

.cb.checked .cb-box {
  border-color: #2f5b8c;
  background: #2f5b8c;
}

.cb.checked .cb-box::after {
  content: "✓";
  color: #fff;
  font-size: 0.75rem;
  line-height: 1;
}

.cb.partial .cb-box {
  border-color: #2f5b8c;
}

.cb.partial .cb-box::after {
  content: "";
  width: 9px;
  height: 2px;
  background: #2f5b8c;
}

.atomic-summary {
  display: flex;
  align-items: flex-start;
  margin-top: 1rem;
  padding: 0.625rem 0.875rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  background: #f8f9fa;
}

.atomic-summary.ready {
  border-color: #f0d98a;
  background: #fff8df;
}

.atomic-summary > .fas {
  margin-top: 0.2rem;
  color: #6b7280;
}
</style>
